import React, { useRef } from 'react'
import SwiperCore, { Navigation, Pagination, EffectFade, SwiperOptions } from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/react'
import { breakpoint, size } from '../layout/styles'
import { useMediaQuery } from '../helpers/mq'
import styled from 'styled-components'

SwiperCore.use([Navigation, Pagination, EffectFade])

interface CommonSliderProps {
  items: any;
  itemBuilder: (item: any) => any;
  slidesPerView?: number | 'auto';
  spaceBetween?: number;
  slideActions?: (swiper: any) => void;
  breakpoints?: {[width: string]: SwiperOptions;}
  className?: string | undefined;
  slideWidth?: any
  usePagination?: boolean
}

/**
 * `CommonSlider` Component.
 * 
 * The `CommonSlider` component is a reusable slider/carousel component built using Swiper.
 * It renders a list of items as slides and supports navigation, pagination, and fade effect.
 *
 * Props:
 * - items (any[]): An array of items to be rendered as slides.
 * - itemBuilder (Function): A function that returns a component or element for rendering each item.
 * - slidesPerView (number | 'auto'): Number of slides per view, or 'auto' for automatic adjustment.
 * - spaceBetween (number): Space between slides in px.
 * - slideActions (Function): A function that receives the Swiper instance for performing additional actions.
 * - breakpoints (Object): An object defining SwiperOptions for various breakpoints.
 * - className (string | undefined): Optional className for the Swiper component.
 * - slideWidth (any): Width of each slide.
 *
 * Usage:
 * ```jsx
 * <CommonSlider
 *    items={items}
 *    itemBuilder={(item) => <SomeComponent item={item} />}
 * />
 * ```
 * 
 * Behavior:
 * - If there is only one item, the component renders a single slide without Swiper functionalities.
 * - It calculates the number of slides per view based on breakpoints and the length of the items.
 * - Navigation arrows are conditionally rendered based on the number of items and the current view.
 * - Supports keyboard navigation and custom pagination.
 * - All Swiper functionalities can be customized by passing SwiperOptions through the `breakpoints` prop.
 */
const CommonSlider = ({ items, itemBuilder, slidesPerView = 3, spaceBetween = 0, slideActions, breakpoints, className, slideWidth, usePagination = false }: CommonSliderProps) => {
  const desktop = useMediaQuery(breakpoint.desktop)
  const tablet = useMediaQuery(breakpoint.tablet)
  const swiperRef = useRef<SwiperCore>();

  if (items.length === 1) {
    return (
      <CarouselContent>
        {itemBuilder(items[0])}
      </CarouselContent>
    )
  }

  // Map the size object to an object with numeric values
  const numericSizes = Object.fromEntries(
    Object.entries(size).map(([key, value]) => [key, parseInt(value, 10)])
  );

  const swiperBreakpoints = {
    [numericSizes.tablet]: {
      slidesPerView: 2
    },
    [numericSizes.desktop]: {
      slidesPerView: items.length >= 3 ? slidesPerView : 2,
    },
  };

  let showNavArrows = true
  if (desktop) {
    showNavArrows = items.length > slidesPerView
  }
  else if (tablet) {
    showNavArrows = items.length > (slidesPerView - 1)
  }

  let pagination = {
    type: 'fraction',
    renderFraction: (_, __) => null,
  }

  if (usePagination) {
    pagination = {
      clickable: true,
      type: 'bullets',
    }
  }

  return (
    <CarouselContent>
      <Swiper
        onBeforeInit={(swiper: SwiperCore): void => {
          swiperRef.current = swiper;
        }}
        className={className}
        breakpoints={breakpoints ?? swiperBreakpoints}
        spaceBetween={spaceBetween}
        slidesPerView={'auto'}
        watchSlidesVisibility={true}
        watchSlidesProgress={true}
        fadeEffect={{ crossFade: true }}
        onSwiper={slideActions}
        keyboard={{
          enabled: true
        }}
        style={{ marginLeft: "1.875rem", marginRight: "1.875rem", position: "unset" }}
        pagination={pagination}>
        {items.map((item: any, index: number) => (
          <SwiperSlide
            key={item.title ?? index}
            data-light={item.text_style && item.text_style === 'Light'}
            style={{ height: 'auto', width: slideWidth ?? 'auto' }}>
            {itemBuilder(item)}
          </SwiperSlide>
        ))}
        {showNavArrows &&
          <>
            <NavIconPrev onClick={() => swiperRef.current?.slidePrev()}/>
            <NavIconNext onClick={() => swiperRef.current?.slideNext()} />
          </>
        }
      </Swiper>
    </CarouselContent>
  )
}

export default CommonSlider

const CarouselContent = styled.div<any>`
  display: flex;
  justify-content: center;
  position: relative;
  max-width: var(--container-width);
  margin: auto;
`

const NavIcon = styled.button<any>`
  position: absolute;
  display: block;
  width: 25px;
  height: 25px;
  margin: auto 0;
  top: 0;
  bottom: 0;
  border: 5px solid #2a007c;
  border-top: none;
  border-left: none;
  opacity: .25;
  color: #666;
  rotate: -45deg;
  transform: skew(-10deg,-10deg);
  z-index: 1;
  cursor: pointer;
  &:disabled{
    display: none;
  }
`

const NavIconPrev = styled(NavIcon)`
  left: 0;
  rotate: 135deg;
`

const NavIconNext = styled(NavIcon)`
  right: 0;
`
